import {Card} from 'platform/components';
import {Box, Show} from 'platform/foundation';

import {useState} from 'react';

import {isNil, mergeAll} from 'ramda';
import {isNotNilOrEmpty} from 'ramda-adjunct';

import i18n from '@dms/i18n';
import {tiresInventoryRoutes} from '@dms/routes';
import {DmsDataGrid, type DmsDataGridProps, TireSetDetail} from '@dms/shared';

import {composePath} from 'shared/web';

import type {ActionCallback, QueryFilterObject} from 'features/eag-grid';

interface CustomerTireSetsProps extends Pick<DmsDataGridProps, 'maximization'> {
  customerId: string;
}

const PREDEFINED_COLUMNS = [
  'tireManufacturer',
  'tireSeason',
  'tireOrderId',
  'id',
  'setNumber',
] as const;

export function CustomerTireSets(props: CustomerTireSetsProps) {
  const [editTireSetData, setEditTireSetData] = useState<{
    orderId: string;
    setId: string;
    title: string;
    refreshData: () => void;
  } | null>(null);

  const queryModifier = (filter: QueryFilterObject) =>
    mergeAll([
      filter,
      {
        customerId: props.customerId ? props.customerId : undefined,
      },
    ]);

  const actionCallback: ActionCallback<typeof PREDEFINED_COLUMNS> = ({
    actionKey,
    rowId,
    getColumnData,
    refreshData,
  }) => {
    const tireManufacturer = getColumnData<{label: string}>('tireManufacturer')?.value?.label;
    const tireSeason = getColumnData<{label: string}>('tireSeason')?.value?.label;
    const orderId = getColumnData<string>('tireOrderId')?.value;
    const orderSetId = Array.isArray(rowId) ? rowId[0] : rowId;
    const setNumber = getColumnData<string>('setNumber')?.value;

    if (actionKey === 'redirectDetail') {
      const titleData = [tireManufacturer, tireSeason].filter(isNotNilOrEmpty);

      if (isNil(orderId) || isNil(orderSetId)) {
        throw new Error('orderId or orderSetId is undefined');
      }

      setEditTireSetData({
        orderId,
        setId: orderSetId,
        title: `${setNumber} - ${titleData.join(', ')}`,
        refreshData,
      });
    }

    if (actionKey === 'redirectTireOrder') {
      window
        .open(
          composePath(tiresInventoryRoutes.tireOrderDetail, {
            params: {id: orderId},
            queryParams: {source: 'tire-list'},
          }),
          '_blank'
        )
        ?.focus();
    }
  };

  return (
    <>
      <Box>
        <Card title={i18n.t('page.tiresInventory.labels.tireSets')}>
          <DmsDataGrid
            maximization={props.maximization}
            data-testid="tireSets-grid"
            gridCode="customer-tire-sets"
            autoHeight
            actionCallback={actionCallback}
            predefinedColumns={PREDEFINED_COLUMNS}
            queryModifier={queryModifier}
          />
        </Card>
      </Box>

      <Show when={editTireSetData}>
        {(editTireSetData) => (
          <TireSetDetail
            tireOrderId={editTireSetData.orderId}
            tireSetId={editTireSetData.setId}
            onClose={() => setEditTireSetData(null)}
            onEdit={() => {
              editTireSetData.refreshData();
              setEditTireSetData(null);
            }}
          />
        )}
      </Show>
    </>
  );
}
